// @ts-nocheck
// Exact live (2026-10-15) version restored from the production sourcemap.
// UX review 2026-10-26: month-labelled hours metric (O04) and a to-do block instead of nav-duplicating tiles (O05/O06).
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Clock, CloseCircle, DocumentText, EmptyWalletTick, InfoCircle, TickCircle } from 'iconsax-react';
import { HoursTable } from './table/HoursTable';
import { ProfileCard } from './cards/ProfileCard';
import { ActionCard } from './cards/ActionCard';
import { HStatsCard } from './cards/HStatsCard';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { getDashboardDetails } from '@application/features/dashboard/dashboardThunk';
import { APIStatus } from '@application/common/enums/api-status';
import { HoursStatus } from '@application/common/enums/hours-status';
import TimeSheetApi from '@infrastructure/timeSheet-api';
import { useFreelancerTaskCounts } from '@ui/components/freelancer/navebar/useFreelancerTaskCounts';
import './freelancer-home.scss';
const plural = (count, singular, pluralLabel) => `${count} ${count === 1 ? singular : pluralLabel}`;
/** Month names of timesheets with the given status (only fetched when the count is > 0; used for rejected months), via the existing hours-by-status API, formatted in English on the client. */
const useTimeSheetPeriods = (status, count) => {
    const [periods, setPeriods] = useState([]);
    useEffect(() => {
        if (!count) {
            setPeriods([]);
            return;
        }
        let active = true;
        TimeSheetApi.getTimeSheetsUsingStatus(status)
            .then((response) => {
            const items = (response.data?.timeSheetWithClientInfo ?? [])
                .filter((item) => item.year >= 1 && item.month >= 1 && item.month <= 12)
                .sort((a, b) => a.year - b.year || a.month - b.month);
            const labels = [...new Set(items.map((item) => moment({ year: item.year, month: item.month - 1, day: 1 }).locale('en').format('MMMM YYYY')))];
            if (active)
                setPeriods(labels);
        })
            .catch(() => {
            if (active)
                setPeriods([]);
        });
        return () => {
            active = false;
        };
    }, [status, count]);
    return periods;
};
export const FreelancerHome = () => {
    const { t } = useTranslation('faqtoring');
    const dispatch = useAppDispatch();
    const { currentUser } = useAppSelector((state) => state.auth);
    const { dashboardDetail, status } = useAppSelector((state) => state.dashboard);
    useEffect(() => {
        dispatch(getDashboardDetails());
    }, []);
    const { openTimeSheets, rejectedTimeSheets, documentsToSign, isLoaded } = useFreelancerTaskCounts();
    const rejectedPeriods = useTimeSheetPeriods(HoursStatus.Rejected, rejectedTimeSheets);
    const tasks = [];
    if (rejectedTimeSheets) {
        tasks.push(_jsx(ActionCard, { title: "Correct rejected hours", caption: rejectedPeriods.length > 0 ? `Rejected: ${rejectedPeriods.join(', ')}` : plural(rejectedTimeSheets, 'rejected timesheet', 'rejected timesheets'), icon: _jsx(CloseCircle, { size: 22 }), to: "/hours-logging" }, "rejected"));
    }
    if (openTimeSheets) {
        tasks.push(_jsx(ActionCard, { title: "Submit your hours", caption: `${plural(openTimeSheets, 'open monthly timesheet', 'open monthly timesheets')} not yet submitted`, icon: _jsx(Clock, { size: 22 }), to: "/hours-logging" }, "open"));
    }
    if (documentsToSign) {
        tasks.push(_jsx(ActionCard, { title: "Sign your documents", caption: `${plural(documentsToSign, 'document', 'documents')} waiting for your signature`, icon: _jsx(DocumentText, { size: 22 }), to: "/documents" }, "documents"));
    }
    if (tasks.length === 0) {
        const unknown = openTimeSheets === undefined || documentsToSign === undefined;
        tasks.push(_jsx(ActionCard, !isLoaded
            ? { title: 'Checking your tasks…', icon: _jsx(TickCircle, { size: 22 }) }
            : unknown
                ? { title: 'Tasks could not be checked', caption: 'Open Hour registration or Documents to see what is still open', icon: _jsx(InfoCircle, { size: 22 }) }
                : { title: "You're all caught up", caption: 'No open tasks right now', icon: _jsx(TickCircle, { size: 22 }) }, "none"));
    }
    return (_jsxs("div", { className: "freelancer-home", children: [_jsxs("header", { className: "freelancer-home__header", children: [_jsxs("div", { className: "freelancer-home__greeting", children: [_jsx("p", { className: "freelancer-home__eyebrow", children: "Overview" }), _jsxs("h1", { children: ["Hello", currentUser?.name ? `, ${currentUser.name}` : ''] })] }), _jsxs("div", { className: "freelancer-home__metrics", "aria-label": "Dashboard totals", children: [_jsx(HStatsCard, { icon: _jsx(Clock, { size: 20 }), title: `Hours recorded in ${moment().locale('en').format('MMMM')}`, color: "secondary", subtitle: dashboardDetail?.totalMonthlyHours?.toString() ?? '—' }), _jsx(HStatsCard, { icon: _jsx(EmptyWalletTick, { size: 20 }), title: t('invoicesTitle'), caption: "Invoices & Faqtoring", color: "secondary", subtitle: dashboardDetail?.totalInvoiceCount?.toString() ?? '—', to: "/invoicing" })] })] }), status === APIStatus.FAILED && _jsx("p", { className: "freelancer-home__notice", role: "status", children: dashboardDetail ? 'Could not refresh the dashboard. Showing the last available data.' : 'Dashboard data is unavailable. Please try again later.' }), _jsxs("div", { className: "freelancer-home__workspace", children: [_jsx(HoursTable, {}), _jsx(ProfileCard, {})] }), _jsxs("section", { className: "freelancer-home__todo", "aria-labelledby": "freelancer-home-todo-title", children: [_jsx("h2", { id: "freelancer-home-todo-title", className: "freelancer-home__todo-title", children: "To do" }), _jsx("div", { className: "freelancer-home__shortcuts freelancer-home__todo-list", children: tasks })] })] }));
};
